/**
 * Elo Leaderboard — Ranked list of all users by Elo rating.
 */
import { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE, TRACKING } from '@/constants/designTokens';
import { ArrowLeft, Zap } from 'lucide-react-native';
import { supabase } from '@/lib/supabase';
import { getEloLeaderboard } from '@/lib/elo';

function getRankTier(rating: number): { label: string; color: string; emoji: string } {
  if (rating >= 2200) return { label: 'GRANDMASTER', color: '#FFD700', emoji: '👑' };
  if (rating >= 2000) return { label: 'MASTER', color: '#C0392B', emoji: '🔴' };
  if (rating >= 1800) return { label: 'DIAMOND', color: '#3498DB', emoji: '💎' };
  if (rating >= 1600) return { label: 'PLATINUM', color: '#1ABC9C', emoji: '💠' };
  if (rating >= 1400) return { label: 'GOLD', color: '#F39C12', emoji: '🥇' };
  if (rating >= 1200) return { label: 'SILVER', color: '#95A5A6', emoji: '🥈' };
  if (rating >= 1000) return { label: 'BRONZE', color: '#CD7F32', emoji: '🥉' };
  return { label: 'IRON', color: '#7F8C8D', emoji: '⚙️' };
}

interface LeaderboardEntry {
  profile_id: string;
  rating: number;
  wins: number;
  losses: number;
  draws: number;
  peak_rating: number;
  display_name?: string;
  handle?: string;
}

export default function EloLeaderboardScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const c = theme.colors;

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await getEloLeaderboard('overall', 100);
      if (!data || data.length === 0) {
        setEntries([]);
        return;
      }

      // Fetch profile names
      const ids = data.map((d: any) => d.profile_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, handle')
        .in('id', ids);

      const nameMap: Record<string, { display_name?: string; handle?: string }> = {};
      for (const p of profiles ?? []) {
        nameMap[p.id] = { display_name: p.display_name, handle: p.handle };
      }

      setEntries(
        data.map((d: any) => ({
          ...d,
          display_name: nameMap[d.profile_id]?.display_name,
          handle: nameMap[d.profile_id]?.handle,
        })),
      );
    } catch (err) {
      console.warn('Failed to load leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Zap size={18} color={c.accent} />
        <Text style={[styles.headerTitle, { color: c.text }]}>ELO LEADERBOARD</Text>
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={c.accent} size="large" />
        </View>
      ) : entries.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyText, { color: c.textSecondary }]}>
            No rated players yet. Complete a duel to get ranked!
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.list}>
          {entries.map((entry, idx) => {
            const tier = getRankTier(entry.rating);
            const isMe = entry.profile_id === profile?.id;
            const name = entry.display_name || entry.handle || 'Anonymous';
            const totalGames = entry.wins + entry.losses + entry.draws;

            return (
              <View
                key={entry.profile_id}
                style={[
                  styles.row,
                  {
                    backgroundColor: isMe ? c.accent + '15' : c.surface,
                    borderColor: isMe ? c.accent : c.border,
                  },
                ]}>
                {/* Rank */}
                <View style={styles.rankCol}>
                  <Text style={[styles.rank, { color: idx < 3 ? '#FFD700' : c.textSecondary }]}>
                    {idx + 1}
                  </Text>
                </View>

                {/* Tier emoji */}
                <Text style={styles.tierEmoji}>{tier.emoji}</Text>

                {/* Name + tier */}
                <View style={styles.nameCol}>
                  <Text style={[styles.name, { color: c.text }]} numberOfLines={1}>
                    {name}
                    {isMe ? ' (you)' : ''}
                  </Text>
                  <Text style={[styles.tierLabel, { color: tier.color }]}>{tier.label}</Text>
                </View>

                {/* Rating */}
                <View style={styles.ratingCol}>
                  <Text style={[styles.ratingValue, { color: c.text }]}>{entry.rating}</Text>
                  <Text style={[styles.record, { color: c.textSecondary }]}>
                    {entry.wins}W {entry.losses}L
                    {entry.draws > 0 ? ` ${entry.draws}D` : ''}
                  </Text>
                </View>
              </View>
            );
          })}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: SPACE.lg,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  backBtn: { marginRight: 4 },
  headerTitle: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
    letterSpacing: TRACKING.wide,
    textTransform: 'uppercase',
  },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  emptyText: { fontSize: FONT.base, fontFamily: TYPO.body.regular, textAlign: 'center' },
  list: { paddingHorizontal: SPACE.lg, paddingVertical: SPACE.md, gap: 8, paddingBottom: 40 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 10,
  },
  rankCol: { width: 30, alignItems: 'center' },
  rank: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold },
  tierEmoji: { fontSize: 22 },
  nameCol: { flex: 1 },
  name: { fontSize: FONT.base, fontFamily: TYPO.body.semibold },
  tierLabel: { fontSize: 10, fontFamily: TYPO.body.semibold, letterSpacing: 1, marginTop: 2 },
  ratingCol: { alignItems: 'flex-end' },
  ratingValue: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold },
  record: { fontSize: 11, fontFamily: TYPO.body.regular, marginTop: 2 },
});
